import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import {
  Session,
  usePlan,
  useSessionProductLines,
  OrderStatus,
  useCreditBalance,
  useBillingInformationsByOrga,
  BillingMethod,
} from '@app/api';

import { InformationBox, SquareButton, useBoolModalState } from '@app/core';
import { PlanBillingSummary, SessionBoxInfo } from 'components';
import { BillingCardLine } from './BillingCardLine';
import { EditableBillingNote } from './EditableBillingNote';
import { SessionInvoices } from '../SessionBillingDetailsModal/SessionInvoices';
import { AddBillingTableProductLine } from './AddBillingTableProductLine';
import { toEuros } from 'lib/numbers';
import classNames from 'classnames';
import { useCountByCountryGroup } from './hooks/useCountByCountryGroup';
import {
  convertOrderProductLine,
  getProductLinesWithGroupQuantity,
  PriceProductLine,
  sumProductLines,
} from 'lib/priceComputation';
import {
  CompareProductLine,
  BillingCardProductLine,
} from './BillingCardProductLine';
import { useRelevantPlanProducts } from 'lib/planAndOffer';
import { shouldApplyVatToBillingInfo } from 'lib/billing';
import { InvoicingButtons } from './Invoicing/InvoicingButtons';

const useStyles = createUseStyles((theme: Theme) => ({
  table: {
    width: '100%',
    borderSpacing: `0 ${theme.marginBase}px`,
    borderCollapse: 'separate',
    textAlign: 'right',

    '& tr:nth-child(even)': {
      backgroundColor: theme.colors.grayBackground,
    },
    '& td': {
      height: theme.marginBase * 7,
    },
    '& td, & th': {
      padding: [[theme.marginBase, theme.marginBase * 2]],
    },
  },
  titleLine: {
    ...theme.fonts.label,
    color: theme.colors.inkPrimary,
    textAlign: 'right',
    '& th': {
      borderBottom: {
        color: theme.colors.grayBorder,
        style: 'solid',
        width: 1,
      },
    },
  },
  productTitle: {
    textAlign: 'left',
  },
  textArea: {
    marginBottom: theme.marginBase,
  },
  sumbitLine: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.marginBase * 2,
  },
  note: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
    whiteSpace: 'pre-wrap',
  },
  orders: {
    marginTop: theme.marginBase * 8,
  },
  orderTitle: {
    ...theme.fonts.h4,
  },
  buttons: {
    marginTop: theme.marginBase * 2,
  },
  extraInfos: {
    display: 'flex',
    gap: theme.marginBase * 3,
    marginTop: theme.marginBase * 2,
    marginBottom: theme.marginBase * 2,

    alignItems: 'stretch',
  },
  extraInfosBlock: {
    padding: theme.marginBase * 2,
    backgroundColor: theme.colors.grayBackground,
    borderRadius: theme.borderRadius.std,
    ...theme.fonts.label,
  },
  icon: {
    fontSize: theme.iconSize.small,
    '&.ri-check-line': {
      color: theme.colors.green,
    },
    '&.ri-close-line': {
      color: theme.colors.red,
    },
  },
}));

interface Props {
  className?: string;
  session?: Session;
}

const completedStatus = [OrderStatus.COMPLETED];

export const BillingCard = ({ className, session }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { id: sessionId, planId } = session || {};
  const { data: plan } = usePlan(planId);
  const countByPriceGroup = useCountByCountryGroup(session?.id);

  const { data: balance } = useCreditBalance(session?.organizationId);
  const { data: billingInformation } = useBillingInformationsByOrga(
    session?.organizationId,
  );
  const shouldApplyVat = useMemo(
    () => shouldApplyVatToBillingInfo(billingInformation),
    [billingInformation],
  );

  const [adding, openNote, close] = useBoolModalState();

  const products = useRelevantPlanProducts(planId, session?.id);
  const { data: completedProductLines } = useSessionProductLines(
    session?.organizationId,
    session?.id,
    completedStatus,
  );

  const planProductLine = useMemo(
    () =>
      getProductLinesWithGroupQuantity(
        products,
        countByPriceGroup,
        session?.duration,
      ),
    [products, session, countByPriceGroup],
  );

  const ordersLines = useMemo(
    () => convertOrderProductLine(completedProductLines, session?.duration),
    [completedProductLines, session],
  );
  const totalNoTax = useMemo(() => sumProductLines(ordersLines), [ordersLines]);
  const taxes = shouldApplyVat ? totalNoTax * 0.2 : 0;

  const mergedLines = useMemo(() => {
    const resultsMap: Record<string, CompareProductLine> = {};
    const hash = (l: PriceProductLine) => `${l.product.id}-${l.unitPrice}`;

    for (const line of ordersLines) {
      resultsMap[hash(line)] = {
        ...line,
        compareToQuantity: 0,
      };
    }

    for (const line of planProductLine) {
      if (line.unitPrice === 0) {
        continue;
      }
      if (resultsMap[hash(line)]) {
        resultsMap[hash(line)].compareToQuantity = line.quantity;
        continue;
      }
      resultsMap[hash(line)] = {
        ...line,
        quantity: 0,
        compareToQuantity: line.quantity,
      };
    }

    return Object.values(resultsMap);
  }, [ordersLines, planProductLine]);

  return (
    <SessionBoxInfo
      className={className}
      color="red"
      title={t('admin.billing.title')}
      icon="ri-file-list-3-line"
      actionButton={
        <SquareButton onClick={openNote} color="purple" icon="ri-edit-line" />
      }
    >
      {session && (
        <EditableBillingNote
          editing={adding}
          requestCloseEditing={close}
          session={session}
        />
      )}
      {!!billingInformation?.billingInstructions && (
        <InformationBox>
          {billingInformation.billingInstructions}
        </InformationBox>
      )}

      <table className={classes.table}>
        <thead>
          <tr className={classes.titleLine}>
            <th className={classes.productTitle}>
              {t('admin.billing.table.product')}
            </th>
            <th>{t('admin.billing.table.price')}</th>
            <th>{t('admin.billing.table.quantity')}</th>
            <th>{t('admin.billing.table.total')}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {mergedLines.map((line) => (
            <BillingCardProductLine
              key={line.product.id}
              defaultName="/"
              productLine={line}
              sessionId={sessionId}
            />
          ))}
          <AddBillingTableProductLine sessionId={sessionId} />

          <BillingCardLine
            title={t('admin.billing.table.total')}
            compareToTotal={sumProductLines(planProductLine)}
            total={totalNoTax}
          />
        </tbody>
      </table>
      <div className={classes.extraInfos}>
        {session && (
          <PlanBillingSummary
            session={session}
            creditBalance={balance}
            priceTotals={{
              total: totalNoTax + taxes,
              totalNoTax: totalNoTax,
              taxes: taxes,
            }}
            isAdminBilling
            readonly
          />
        )}
        {plan?.billingMethod !== BillingMethod.ORG_CREDIT && (
          <div className={classes.extraInfosBlock}>
            {balance &&
              t('admin.billing.remainingCredit', {
                amount: toEuros(balance.amount),
              })}
          </div>
        )}
        <div className={classes.extraInfosBlock}>
          <div>
            <i
              className={classNames(classes.icon, {
                'ri-check-line':
                  !!billingInformation?.billingInformationValidated,
                'ri-close-line':
                  !billingInformation?.billingInformationValidated,
              })}
            />{' '}
            {t('admin.billing.billingInfoValidated')}
          </div>
          <div>
            <i
              className={classNames(classes.icon, {
                'ri-check-line': !billingInformation?.preventAutomaticBilling,
                'ri-close-line': !!billingInformation?.preventAutomaticBilling,
              })}
            />{' '}
            {t('admin.billing.autoBilling')}
          </div>
          <div>
            <i
              className={classNames(classes.icon, {
                'ri-check-line':
                  !billingInformation?.requirePurchaseOrderNumber,
                'ri-close-line':
                  !!billingInformation?.requirePurchaseOrderNumber,
              })}
            />{' '}
            {t('admin.billing.noPurchaseOrderRequired')}
          </div>
        </div>
      </div>
      <InvoicingButtons session={session} className={classes.buttons} />

      {sessionId && <SessionInvoices sessionId={sessionId} />}
    </SessionBoxInfo>
  );
};
